// Tiny in-memory fake backend. Used only when demo mode is on (see
// src/app/axiosBaseQuery.js) so the app is fully clickable without the
// Laravel API running. State resets on page reload — this is a demo aid,
// not a persistence layer. Swap VITE_DEMO_MODE=false once the real
// backend is ready; nothing else in the app needs to change since every
// feature slice already talks through axiosBaseQuery.
import {
  initialUsers,
  initialKycRecords,
  initialInvestments,
  initialAuditLogs,
  initialPlans,
  initialPayments,
  initialPayouts,
  initialNotifications,
  initialTickets,
} from './fixtures';

const clone = (v) => JSON.parse(JSON.stringify(v));

// module-scoped "database" — mutated in place as the demo is clicked through
const db = {
  users: clone(initialUsers),
  kycRecords: clone(initialKycRecords),
  investments: clone(initialInvestments),
  auditLogs: clone(initialAuditLogs),
  plans: clone(initialPlans),
  payments: clone(initialPayments),
  payouts: clone(initialPayouts),
  notifications: clone(initialNotifications),
  tickets: clone(initialTickets),
  nextUserId: 100,
  nextKycId: 100,
  nextInvestmentId: 100,
  nextAuditId: 100,
  nextPlanId: 100,
  nextPaymentId: 100,
  nextPayoutId: 100,
  nextNotificationId: 100,
  nextTicketId: 100,
  nextTicketMessageId: 100,
};

const SESSION_KEY = 'canvainvest_demo_session_user_id';

function getSessionUserId() {
  const raw = window.localStorage.getItem(SESSION_KEY);
  return raw ? Number(raw) : null;
}

function setSessionUserId(id) {
  if (id == null) window.localStorage.removeItem(SESSION_KEY);
  else window.localStorage.setItem(SESSION_KEY, String(id));
}

function currentUser() {
  const id = getSessionUserId();
  return id ? db.users.find((u) => u.id === id) : null;
}

function requireAuth() {
  const user = currentUser();
  if (!user) throw apiError(401, 'Unauthenticated.');
  return user;
}

function apiError(status, message, errors) {
  const err = new Error(message);
  err.status = status;
  err.data = { message, ...(errors ? { errors } : {}) };
  return err;
}

function readField(data, key, fallback = undefined) {
  if (!data) return fallback;
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    return data.has(key) ? data.get(key) : fallback;
  }
  return key in data ? data[key] : fallback;
}

function paginate(list, { page = 1, per_page = 20 } = {}) {
  page = Number(page) || 1;
  per_page = Number(per_page) || 20;
  const total = list.length;
  const last_page = Math.max(1, Math.ceil(total / per_page));
  const start = (page - 1) * per_page;
  return {
    data: list.slice(start, start + per_page),
    meta: { current_page: page, last_page, total },
  };
}

function userResource(user) {
  const kyc = db.kycRecords.find((k) => k.user_id === user.id);
  return {
    id: user.id,
    full_name: user.full_name,
    email: user.email,
    phone: user.phone,
    roles: user.roles,
    kyc_status: kyc?.status ?? user.kyc_status ?? null,
    is_active: user.is_active,
    email_verified_at: user.email_verified_at,
    created_manually_by_admin: user.created_manually_by_admin,
    created_at: user.created_at,
  };
}

function investmentResource(inv) {
  const investor = db.users.find((u) => u.id === inv.investor_id);
  return { ...inv, investor_name: investor?.full_name ?? inv.investor_name };
}

function planResource(plan) {
  return { ...plan };
}

function paymentResource(payment) {
  const inv = db.investments.find((i) => i.id === payment.investment_id);
  return { ...payment, investment_reference: inv?.reference ?? null, investor_name: db.users.find((u) => u.id === payment.investor_id)?.full_name ?? null };
}

function payoutResource(payout) {
  const inv = db.investments.find((i) => i.id === payout.investment_id);
  return { ...payout, investment_reference: inv?.reference ?? null, investor_name: db.users.find((u) => u.id === payout.investor_id)?.full_name ?? null };
}

function ticketResource(ticket) {
  return { ...ticket };
}

function notify(userId, type, title, message) {
  db.notifications.unshift({
    id: db.nextNotificationId++,
    user_id: userId,
    type,
    title,
    message,
    read_at: null,
    created_at: new Date().toISOString(),
  });
}

function addMonths(dateStr, months) {
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

// --- OTP / 2FA helpers (registration email verification, password-reset
// codes, and admin 2FA all share this shape). A real backend would email/SMS
// the code instead of returning it — the mock returns `demo_otp` in the
// response purely so the frontend demo can display it without a mail server. ---
function genOtp() {
  return String(Math.floor(100000 + Math.random() * 900000));
}
function minutesFromNow(n) {
  return new Date(Date.now() + n * 60000).toISOString();
}
function isExpired(iso) {
  return !iso || new Date(iso).getTime() < Date.now();
}
function randomToken() {
  return Array.from({ length: 24 }, () => Math.floor(Math.random() * 36).toString(36)).join('');
}

const routes = [
  // --- Auth ---
  ['POST', /^\/auth\/register$/, ({ data }) => {
    const email = readField(data, 'email');
    if (db.users.some((u) => u.email.toLowerCase() === String(email).toLowerCase())) {
      throw apiError(422, 'The email has already been taken.', { email: ['The email has already been taken.'] });
    }
    const otp = genOtp();
    const user = {
      id: db.nextUserId++,
      full_name: readField(data, 'full_name'),
      email,
      password: readField(data, 'password'),
      phone: readField(data, 'phone', null),
      roles: ['investor'],
      kyc_status: 'pending',
      is_active: true,
      email_verified_at: null,
      created_manually_by_admin: false,
      created_at: new Date().toISOString(),
      email_otp_code: otp,
      email_otp_expires_at: minutesFromNow(10),
      email_otp_attempts: 0,
      email_otp_last_sent_at: new Date().toISOString(),
    };
    db.users.push(user);
    return { status: 201, body: { message: 'Registration successful — verify your email to continue.', data: userResource(user), demo_otp: otp } };
  }],

  // --- Registration email verification (OTP) ---
  ['POST', /^\/auth\/email\/otp\/verify$/, ({ data }) => {
    const email = String(readField(data, 'email', '')).toLowerCase();
    const code = readField(data, 'code');
    const user = db.users.find((u) => u.email.toLowerCase() === email);
    if (!user) throw apiError(404, 'Account not found.');
    if (user.email_verified_at) return { body: { message: 'Email already verified.', data: userResource(user) } };
    if ((user.email_otp_attempts ?? 0) >= 5) throw apiError(422, 'Too many attempts. Request a new code.', { code: ['Too many attempts. Request a new code.'] });
    if (isExpired(user.email_otp_expires_at) || user.email_otp_code !== code) {
      user.email_otp_attempts = (user.email_otp_attempts ?? 0) + 1;
      throw apiError(422, 'That code is incorrect or has expired.', { code: ['That code is incorrect or has expired.'] });
    }
    user.email_verified_at = new Date().toISOString();
    user.email_otp_code = null;
    user.email_otp_expires_at = null;
    user.email_otp_attempts = 0;
    return { body: { message: 'Email verified.', data: userResource(user) } };
  }],

  ['POST', /^\/auth\/email\/otp\/resend$/, ({ data }) => {
    const email = String(readField(data, 'email', '')).toLowerCase();
    const user = db.users.find((u) => u.email.toLowerCase() === email);
    if (!user) throw apiError(404, 'Account not found.');
    if (user.email_otp_last_sent_at && Date.now() - new Date(user.email_otp_last_sent_at).getTime() < 60000) {
      throw apiError(429, 'Please wait a moment before requesting another code.');
    }
    const otp = genOtp();
    user.email_otp_code = otp;
    user.email_otp_expires_at = minutesFromNow(10);
    user.email_otp_attempts = 0;
    user.email_otp_last_sent_at = new Date().toISOString();
    return { body: { message: 'A new code has been sent.', demo_otp: otp } };
  }],

  ['POST', /^\/auth\/login$/, ({ data }) => {
    const email = String(readField(data, 'email', '')).toLowerCase();
    const password = readField(data, 'password');
    const user = db.users.find((u) => u.email.toLowerCase() === email);
    if (!user || password !== user.password) {
      throw apiError(422, 'These credentials do not match our records.', {
        email: ['These credentials do not match our records.'],
      });
    }
    if (!user.is_active) {
      throw apiError(422, 'This account has been deactivated. Contact support.', {
        email: ['This account has been deactivated. Contact support.'],
      });
    }

    // Admins and Super Admins complete a second 2FA step before a session is issued.
    if (user.roles.includes('admin') || user.roles.includes('super_admin')) {
      const code = genOtp();
      user.twofa_code = code;
      user.twofa_expires_at = minutesFromNow(5);
      user.twofa_attempts = 0;
      user.twofa_last_sent_at = new Date().toISOString();
      return { body: { message: 'Enter the code sent to your device to finish signing in.', requires_2fa: true, user_id: user.id, email: user.email, demo_otp: code } };
    }

    setSessionUserId(user.id);
    return { body: { message: 'Logged in successfully.', data: userResource(user) } };
  }],

  // --- Admin / Super Admin two-factor login step ---
  ['POST', /^\/auth\/2fa\/verify$/, ({ data }) => {
    const userId = Number(readField(data, 'user_id'));
    const code = readField(data, 'code');
    const user = db.users.find((u) => u.id === userId);
    if (!user) throw apiError(404, 'Account not found.');
    if ((user.twofa_attempts ?? 0) >= 5) throw apiError(422, 'Too many attempts. Request a new code.', { code: ['Too many attempts. Request a new code.'] });
    if (isExpired(user.twofa_expires_at) || user.twofa_code !== code) {
      user.twofa_attempts = (user.twofa_attempts ?? 0) + 1;
      throw apiError(422, 'That code is incorrect or has expired.', { code: ['That code is incorrect or has expired.'] });
    }
    user.twofa_code = null;
    user.twofa_expires_at = null;
    user.twofa_attempts = 0;
    setSessionUserId(user.id);
    return { body: { message: 'Logged in successfully.', data: userResource(user) } };
  }],

  ['POST', /^\/auth\/2fa\/resend$/, ({ data }) => {
    const userId = Number(readField(data, 'user_id'));
    const user = db.users.find((u) => u.id === userId);
    if (!user) throw apiError(404, 'Account not found.');
    if (user.twofa_last_sent_at && Date.now() - new Date(user.twofa_last_sent_at).getTime() < 60000) {
      throw apiError(429, 'Please wait a moment before requesting another code.');
    }
    const code = genOtp();
    user.twofa_code = code;
    user.twofa_expires_at = minutesFromNow(5);
    user.twofa_attempts = 0;
    user.twofa_last_sent_at = new Date().toISOString();
    return { body: { message: 'A new code has been sent.', demo_otp: code } };
  }],

  ['POST', /^\/auth\/logout$/, () => {
    setSessionUserId(null);
    return { body: { message: 'Logged out.' } };
  }],

  ['GET', /^\/auth\/me$/, () => ({ body: { data: userResource(requireAuth()) } })],

  // --- Password recovery (OTP-secured, two steps: verify code → reset) ---
  ['POST', /^\/auth\/forgot-password$/, ({ data }) => {
    const email = String(readField(data, 'email', '')).toLowerCase();
    const user = db.users.find((u) => u.email.toLowerCase() === email);
    const generic = { message: 'If an account exists for that email, a code has been sent.' };
    if (!user) return { body: generic };
    if (user.reset_otp_last_sent_at && Date.now() - new Date(user.reset_otp_last_sent_at).getTime() < 60000) {
      throw apiError(429, 'Please wait a moment before requesting another code.');
    }
    const otp = genOtp();
    user.reset_otp_code = otp;
    user.reset_otp_expires_at = minutesFromNow(10);
    user.reset_otp_attempts = 0;
    user.reset_otp_last_sent_at = new Date().toISOString();
    return { body: { ...generic, demo_otp: otp } };
  }],

  ['POST', /^\/auth\/password\/otp\/verify$/, ({ data }) => {
    const email = String(readField(data, 'email', '')).toLowerCase();
    const code = readField(data, 'code');
    const user = db.users.find((u) => u.email.toLowerCase() === email);
    if (!user) throw apiError(404, 'Account not found.');
    if ((user.reset_otp_attempts ?? 0) >= 5) throw apiError(422, 'Too many attempts. Request a new code.', { code: ['Too many attempts. Request a new code.'] });
    if (isExpired(user.reset_otp_expires_at) || user.reset_otp_code !== code) {
      user.reset_otp_attempts = (user.reset_otp_attempts ?? 0) + 1;
      throw apiError(422, 'That code is incorrect or has expired.', { code: ['That code is incorrect or has expired.'] });
    }
    user.reset_otp_code = null;
    user.reset_otp_expires_at = null;
    user.reset_otp_attempts = 0;
    const resetToken = randomToken();
    user.reset_token = resetToken;
    user.reset_token_expires_at = minutesFromNow(10);
    return { body: { message: 'Code verified.', reset_token: resetToken } };
  }],

  ['POST', /^\/auth\/reset-password$/, ({ data }) => {
    const email = String(readField(data, 'email', '')).toLowerCase();
    const resetToken = readField(data, 'reset_token');
    const user = db.users.find((u) => u.email.toLowerCase() === email);
    if (!user || isExpired(user.reset_token_expires_at) || !user.reset_token || user.reset_token !== resetToken) {
      throw apiError(422, 'This reset session has expired. Start again.', {});
    }
    user.password = readField(data, 'password');
    user.reset_token = null;
    user.reset_token_expires_at = null;
    return { body: { message: 'Password reset successfully.' } };
  }],

  // --- Investor KYC self-service ---
  ['GET', /^\/kyc$/, () => {
    const user = requireAuth();
    const record = db.kycRecords.find((k) => k.user_id === user.id) ?? null;
    return { body: { data: record } };
  }],

  ['POST', /^\/kyc$/, ({ data }) => {
    const user = requireAuth();
    let record = db.kycRecords.find((k) => k.user_id === user.id);
    const payload = {
      full_name: readField(data, 'full_name'),
      bvn: readField(data, 'bvn'),
      address: readField(data, 'address'),
      id_document_type: readField(data, 'id_document_type'),
      has_id_document: true,
      status: 'pending',
      rejection_reason: null,
      reviewed_by: null,
      reviewed_at: null,
    };
    if (record) {
      Object.assign(record, payload);
    } else {
      record = {
        id: db.nextKycId++,
        user_id: user.id,
        investor_name: user.full_name,
        investor_email: user.email,
        created_at: new Date().toISOString(),
        ...payload,
      };
      db.kycRecords.push(record);
    }
    user.kyc_status = 'pending';
    return { status: 201, body: { message: 'KYC submitted for review.', data: record } };
  }],

  // --- Investor: my investments ---
  ['GET', /^\/me\/investments$/, () => {
    const user = requireAuth();
    const kyc = db.kycRecords.find((k) => k.user_id === user.id);
    if (kyc?.status !== 'approved') {
      return { body: { kyc_required: true, kyc_status: kyc?.status ?? 'pending', data: [] } };
    }
    const mine = db.investments.filter((i) => i.investor_id === user.id).map(investmentResource);
    return { body: { kyc_required: false, data: mine } };
  }],

  // --- Admin: KYC review ---
  ['GET', /^\/admin\/kyc$/, ({ params }) => {
    let list = db.kycRecords;
    if (params?.status) list = list.filter((k) => k.status === params.status);
    return { body: paginate(list, params) };
  }],

  ['GET', /^\/admin\/kyc\/(\d+)$/, ({ m }) => {
    const record = db.kycRecords.find((k) => k.id === Number(m[1]));
    if (!record) throw apiError(404, 'Not found.');
    return { body: { data: record } };
  }],

  ['PATCH', /^\/admin\/kyc\/(\d+)\/approve$/, ({ m }) => {
    const record = db.kycRecords.find((k) => k.id === Number(m[1]));
    if (!record) throw apiError(404, 'Not found.');
    record.status = 'approved';
    record.rejection_reason = null;
    record.reviewed_by = currentUser()?.full_name ?? 'Admin';
    record.reviewed_at = new Date().toISOString();
    const user = db.users.find((u) => u.id === record.user_id);
    if (user) user.kyc_status = 'approved';
    logAudit('kyc.approved', 'KycRecord', record.id, { status: 'pending' }, { status: 'approved' });
    return { body: { message: 'KYC approved.', data: record } };
  }],

  ['PATCH', /^\/admin\/kyc\/(\d+)\/reject$/, ({ m, data }) => {
    const record = db.kycRecords.find((k) => k.id === Number(m[1]));
    if (!record) throw apiError(404, 'Not found.');
    record.status = 'rejected';
    record.rejection_reason = readField(data, 'reason', 'Rejected.');
    record.reviewed_by = currentUser()?.full_name ?? 'Admin';
    record.reviewed_at = new Date().toISOString();
    const user = db.users.find((u) => u.id === record.user_id);
    if (user) user.kyc_status = 'rejected';
    logAudit('kyc.rejected', 'KycRecord', record.id, { status: 'pending' }, { status: 'rejected', rejection_reason: record.rejection_reason });
    return { body: { message: 'KYC rejected.', data: record } };
  }],

  // --- Admin: investors ---
  ['GET', /^\/admin\/investors$/, ({ params }) => {
    let list = db.users.filter((u) => u.roles.includes('investor'));
    if (params?.search) {
      const s = params.search.toLowerCase();
      list = list.filter((u) => u.full_name.toLowerCase().includes(s) || u.email.toLowerCase().includes(s));
    }
    if (params?.kyc_status) {
      list = list.filter((u) => (db.kycRecords.find((k) => k.user_id === u.id)?.status ?? 'pending') === params.kyc_status);
    }
    return { body: paginate(list.map(userResource), params) };
  }],

  ['GET', /^\/admin\/investors\/(\d+)$/, ({ m }) => {
    const user = db.users.find((u) => u.id === Number(m[1]));
    if (!user) throw apiError(404, 'Not found.');
    return { body: { data: userResource(user) } };
  }],

  ['POST', /^\/admin\/investors\/manual$/, ({ data }) => {
    const user = {
      id: db.nextUserId++,
      full_name: readField(data, 'full_name'),
      email: readField(data, 'email'),
      phone: readField(data, 'phone', null),
      roles: ['investor'],
      is_active: true,
      email_verified_at: new Date().toISOString(),
      created_manually_by_admin: true,
      created_at: new Date().toISOString(),
    };
    db.users.push(user);
    const status = readField(data, 'kyc[status]', readField(data, 'kyc.status', 'approved'));
    const record = {
      id: db.nextKycId++,
      user_id: user.id,
      investor_name: user.full_name,
      investor_email: user.email,
      full_name: readField(data, 'kyc[full_name]', readField(data, 'kyc.full_name')),
      bvn: readField(data, 'kyc[bvn]', readField(data, 'kyc.bvn')),
      address: readField(data, 'kyc[address]', readField(data, 'kyc.address')),
      id_document_type: readField(data, 'kyc[id_document_type]', readField(data, 'kyc.id_document_type')),
      has_id_document: true,
      status,
      rejection_reason: null,
      reviewed_by: status !== 'pending' ? currentUser()?.full_name ?? 'Admin' : null,
      reviewed_at: status !== 'pending' ? new Date().toISOString() : null,
      created_at: new Date().toISOString(),
    };
    db.kycRecords.push(record);
    user.kyc_status = status;
    logAudit('investor.created_manually', 'User', user.id, null, { full_name: user.full_name, email: user.email });
    return { status: 201, body: { message: 'Investor and KYC record created.', data: userResource(user) } };
  }],

  ['POST', /^\/admin\/investors$/, ({ data }) => {
    const user = {
      id: db.nextUserId++,
      full_name: readField(data, 'full_name'),
      email: readField(data, 'email'),
      phone: readField(data, 'phone', null),
      roles: ['investor'],
      kyc_status: 'pending',
      is_active: true,
      email_verified_at: null,
      created_manually_by_admin: true,
      created_at: new Date().toISOString(),
    };
    db.users.push(user);
    logAudit('investor.created', 'User', user.id, null, { full_name: user.full_name, email: user.email });
    return { status: 201, body: { message: 'Investor created.', data: userResource(user) } };
  }],

  ['PUT', /^\/admin\/investors\/(\d+)$/, ({ m, data }) => {
    const user = db.users.find((u) => u.id === Number(m[1]));
    if (!user) throw apiError(404, 'Not found.');
    ['full_name', 'email', 'phone', 'is_active'].forEach((key) => {
      const val = readField(data, key, undefined);
      if (val !== undefined) user[key] = val;
    });
    return { body: { message: 'Investor updated.', data: userResource(user) } };
  }],

  ['DELETE', /^\/admin\/investors\/(\d+)$/, ({ m }) => {
    db.users = db.users.filter((u) => u.id !== Number(m[1]));
    return { body: { message: 'Investor removed.' } };
  }],

  ['PATCH', /^\/admin\/investors\/(\d+)\/activate$/, ({ m }) => {
    const user = db.users.find((u) => u.id === Number(m[1]));
    if (!user) throw apiError(404, 'Not found.');
    user.is_active = true;
    logAudit('investor.activated', 'User', user.id, { is_active: false }, { is_active: true });
    return { body: { message: 'Investor activated.', data: userResource(user) } };
  }],

  ['PATCH', /^\/admin\/investors\/(\d+)\/deactivate$/, ({ m }) => {
    const user = db.users.find((u) => u.id === Number(m[1]));
    if (!user) throw apiError(404, 'Not found.');
    user.is_active = false;
    logAudit('investor.deactivated', 'User', user.id, { is_active: true }, { is_active: false });
    return { body: { message: 'Investor deactivated.', data: userResource(user) } };
  }],

  // --- Admin: investments ---
  ['GET', /^\/admin\/investments$/, ({ params }) => {
    let list = db.investments;
    if (params?.investor_id) list = list.filter((i) => String(i.investor_id) === String(params.investor_id));
    if (params?.status) list = list.filter((i) => i.status === params.status);
    return { body: paginate(list.map(investmentResource), params) };
  }],

  ['GET', /^\/admin\/investments\/(\d+)$/, ({ m }) => {
    const inv = db.investments.find((i) => i.id === Number(m[1]));
    if (!inv) throw apiError(404, 'Not found.');
    return { body: { data: investmentResource(inv) } };
  }],

  ['POST', /^\/admin\/investments$/, ({ data }) => {
    const investorId = Number(readField(data, 'investor_id'));
    const principal = Number(readField(data, 'principal_amount'));
    const inv = {
      id: db.nextInvestmentId++,
      reference: `CINV-${new Date().getFullYear()}-${String(db.nextInvestmentId).padStart(6, '0')}`,
      investor_id: investorId,
      plan_name: readField(data, 'plan_name') || 'Fixed Return Plan',
      principal_amount: principal,
      monthly_interest_rate: Number(readField(data, 'monthly_interest_rate')),
      start_date: readField(data, 'start_date'),
      maturity_date: readField(data, 'maturity_date') || null,
      payout_schedule: readField(data, 'payout_schedule'),
      status: 'active',
      total_interest_paid: 0,
      current_value: principal,
      last_accrued_at: null,
      notes: readField(data, 'notes') || null,
      created_at: new Date().toISOString(),
    };
    db.investments.push(inv);
    logAudit('investment.created', 'Investment', inv.id, null, { investor_id: investorId, principal_amount: principal });
    return { status: 201, body: { message: 'Investment created.', data: investmentResource(inv) } };
  }],

  ['PUT', /^\/admin\/investments\/(\d+)$/, ({ m, data }) => {
    const inv = db.investments.find((i) => i.id === Number(m[1]));
    if (!inv) throw apiError(404, 'Not found.');
    ['plan_name', 'principal_amount', 'maturity_date', 'payout_schedule', 'notes'].forEach((key) => {
      const val = readField(data, key, undefined);
      if (val !== undefined) inv[key] = val;
    });
    return { body: { message: 'Investment updated.', data: investmentResource(inv) } };
  }],

  ['PATCH', /^\/admin\/investments\/(\d+)\/rate$/, ({ m, data }) => {
    const inv = db.investments.find((i) => i.id === Number(m[1]));
    if (!inv) throw apiError(404, 'Not found.');
    const before = inv.monthly_interest_rate;
    inv.monthly_interest_rate = Number(readField(data, 'monthly_interest_rate'));
    logAudit('investment.rate_updated', 'Investment', inv.id, { monthly_interest_rate: before }, {
      monthly_interest_rate: inv.monthly_interest_rate,
      reason: readField(data, 'reason', null),
    });
    return { body: { message: 'Interest rate updated.', data: investmentResource(inv) } };
  }],

  ['PATCH', /^\/admin\/investments\/(\d+)\/status$/, ({ m, data }) => {
    const inv = db.investments.find((i) => i.id === Number(m[1]));
    if (!inv) throw apiError(404, 'Not found.');
    const before = inv.status;
    inv.status = readField(data, 'status');
    logAudit('investment.status_updated', 'Investment', inv.id, { status: before }, { status: inv.status });
    return { body: { message: 'Investment status updated.', data: investmentResource(inv) } };
  }],

  ['DELETE', /^\/admin\/investments\/(\d+)$/, ({ m }) => {
    db.investments = db.investments.filter((i) => i.id !== Number(m[1]));
    return { body: { message: 'Investment removed.' } };
  }],

  // --- Plans (public catalogue) ---
  ['GET', /^\/plans$/, () => ({ body: { data: db.plans.filter((p) => p.is_active).map(planResource) } })],

  // --- Admin: plans ---
  ['GET', /^\/admin\/plans$/, () => ({ body: { data: db.plans.map(planResource) } })],

  ['POST', /^\/admin\/plans$/, ({ data }) => {
    const plan = {
      id: db.nextPlanId++,
      name: readField(data, 'name'),
      description: readField(data, 'description', ''),
      duration_months: Number(readField(data, 'duration_months')),
      monthly_interest_rate: Number(readField(data, 'monthly_interest_rate')),
      min_amount: Number(readField(data, 'min_amount')),
      max_amount: Number(readField(data, 'max_amount')),
      payout_schedule: readField(data, 'payout_schedule', 'monthly'),
      is_active: true,
      created_at: new Date().toISOString(),
    };
    db.plans.push(plan);
    logAudit('plan.created', 'InvestmentPlan', plan.id, null, { name: plan.name });
    return { status: 201, body: { message: 'Plan created.', data: planResource(plan) } };
  }],

  ['PUT', /^\/admin\/plans\/(\d+)$/, ({ m, data }) => {
    const plan = db.plans.find((p) => p.id === Number(m[1]));
    if (!plan) throw apiError(404, 'Not found.');
    ['name', 'description', 'duration_months', 'monthly_interest_rate', 'min_amount', 'max_amount', 'payout_schedule'].forEach((key) => {
      const val = readField(data, key, undefined);
      if (val === undefined) return;
      plan[key] = ['duration_months', 'monthly_interest_rate', 'min_amount', 'max_amount'].includes(key) ? Number(val) : val;
    });
    logAudit('plan.updated', 'InvestmentPlan', plan.id, null, { name: plan.name });
    return { body: { message: 'Plan updated.', data: planResource(plan) } };
  }],

  ['PATCH', /^\/admin\/plans\/(\d+)\/archive$/, ({ m }) => {
    const plan = db.plans.find((p) => p.id === Number(m[1]));
    if (!plan) throw apiError(404, 'Not found.');
    plan.is_active = false;
    logAudit('plan.archived', 'InvestmentPlan', plan.id, { is_active: true }, { is_active: false });
    return { body: { message: 'Plan archived.', data: planResource(plan) } };
  }],

  ['PATCH', /^\/admin\/plans\/(\d+)\/activate$/, ({ m }) => {
    const plan = db.plans.find((p) => p.id === Number(m[1]));
    if (!plan) throw apiError(404, 'Not found.');
    plan.is_active = true;
    logAudit('plan.activated', 'InvestmentPlan', plan.id, { is_active: false }, { is_active: true });
    return { body: { message: 'Plan reactivated.', data: planResource(plan) } };
  }],

  // --- Investor: subscribe to a plan (creates a pending investment) ---
  ['POST', /^\/investments$/, ({ data }) => {
    const user = requireAuth();
    const kyc = db.kycRecords.find((k) => k.user_id === user.id);
    if (kyc?.status !== 'approved') throw apiError(403, 'Complete KYC before subscribing to a plan.');

    const plan = db.plans.find((p) => p.id === Number(readField(data, 'plan_id')));
    if (!plan || !plan.is_active) throw apiError(404, 'Plan not found or no longer available.');

    const principal = Number(readField(data, 'principal_amount'));
    if (principal < plan.min_amount || principal > plan.max_amount) {
      throw apiError(422, `Amount must be between ₦${plan.min_amount.toLocaleString()} and ₦${plan.max_amount.toLocaleString()}.`, {
        principal_amount: ['Amount is outside the plan\'s allowed range.'],
      });
    }

    const inv = {
      id: db.nextInvestmentId++,
      reference: `CINV-${new Date().getFullYear()}-${String(db.nextInvestmentId).padStart(6, '0')}`,
      investor_id: user.id,
      plan_id: plan.id,
      plan_name: plan.name,
      principal_amount: principal,
      monthly_interest_rate: plan.monthly_interest_rate,
      start_date: null,
      maturity_date: null,
      payout_schedule: plan.payout_schedule,
      status: 'pending',
      total_interest_paid: 0,
      current_value: principal,
      last_accrued_at: null,
      notes: 'Awaiting funding.',
      created_at: new Date().toISOString(),
    };
    db.investments.push(inv);
    logAudit('investment.subscribed', 'Investment', inv.id, null, { investor_id: user.id, plan_id: plan.id, principal_amount: principal });
    return { status: 201, body: { message: 'Subscription created — fund it to activate.', data: investmentResource(inv) } };
  }],

  // --- Investor: fund a pending investment via simulated Paystack ---
  ['POST', /^\/investments\/(\d+)\/fund\/paystack$/, ({ m }) => {
    const user = requireAuth();
    const inv = db.investments.find((i) => i.id === Number(m[1]) && i.investor_id === user.id);
    if (!inv) throw apiError(404, 'Investment not found.');
    if (inv.status !== 'pending') throw apiError(422, 'This investment is not awaiting funding.');

    const payment = {
      id: db.nextPaymentId++,
      investment_id: inv.id,
      investor_id: user.id,
      amount: inv.principal_amount,
      method: 'paystack',
      reference: `PSK-REF-${Date.now().toString().slice(-8)}`,
      status: 'successful',
      note: null,
      created_at: new Date().toISOString(),
    };
    db.payments.push(payment);
    inv.status = 'active';
    inv.start_date = new Date().toISOString().slice(0, 10);
    inv.maturity_date = db.plans.find((p) => p.id === inv.plan_id)
      ? addMonths(inv.start_date, db.plans.find((p) => p.id === inv.plan_id).duration_months)
      : null;
    inv.notes = null;
    notify(user.id, 'investment.activated', 'Investment activated', `Your ${inv.reference} investment is now active.`);
    logAudit('payment.paystack_success', 'Investment', inv.id, { status: 'pending' }, { status: 'active', reference: payment.reference });
    return { body: { message: 'Payment successful — investment activated.', data: { investment: investmentResource(inv), payment: paymentResource(payment) } } };
  }],

  // --- Admin: record a manual payment against a pending investment ---
  ['POST', /^\/admin\/investments\/(\d+)\/payments$/, ({ m, data }) => {
    const inv = db.investments.find((i) => i.id === Number(m[1]));
    if (!inv) throw apiError(404, 'Investment not found.');
    if (inv.status !== 'pending') throw apiError(422, 'This investment is not awaiting funding.');

    const payment = {
      id: db.nextPaymentId++,
      investment_id: inv.id,
      investor_id: inv.investor_id,
      amount: inv.principal_amount,
      method: 'manual',
      reference: readField(data, 'reference', `MANUAL-${Date.now()}`),
      status: 'successful',
      note: readField(data, 'note', null),
      created_at: new Date().toISOString(),
    };
    db.payments.push(payment);
    inv.status = 'active';
    inv.start_date = new Date().toISOString().slice(0, 10);
    const plan = db.plans.find((p) => p.id === inv.plan_id);
    inv.maturity_date = plan ? addMonths(inv.start_date, plan.duration_months) : null;
    inv.notes = null;
    notify(inv.investor_id, 'investment.activated', 'Investment activated', `Your ${inv.reference} investment is now active.`);
    logAudit('payment.manual_recorded', 'Investment', inv.id, { status: 'pending' }, { status: 'active', reference: payment.reference });
    return { status: 201, body: { message: 'Manual payment recorded — investment activated.', data: { investment: investmentResource(inv), payment: paymentResource(payment) } } };
  }],

  // --- Payouts: Paystack (simulated) and manual, admin-triggered ---
  ['POST', /^\/admin\/investments\/(\d+)\/payouts\/paystack$/, ({ m, data }) => {
    const inv = db.investments.find((i) => i.id === Number(m[1]));
    if (!inv) throw apiError(404, 'Investment not found.');
    if (!['active', 'matured'].includes(inv.status)) throw apiError(422, 'Investment is not eligible for payout.');

    const amount = Number(readField(data, 'amount'));
    const payout = {
      id: db.nextPayoutId++,
      investment_id: inv.id,
      investor_id: inv.investor_id,
      amount,
      method: 'paystack',
      reference: `PSK-OUT-${Date.now().toString().slice(-8)}`,
      status: 'completed',
      note: readField(data, 'note', 'Interest payout.'),
      created_at: new Date().toISOString(),
    };
    db.payouts.push(payout);
    inv.total_interest_paid = (inv.total_interest_paid || 0) + amount;
    inv.last_accrued_at = new Date().toISOString();
    notify(inv.investor_id, 'payout.completed', 'Payout completed', `₦${amount.toLocaleString()} was paid out on ${inv.reference}.`);
    logAudit('payout.paystack_completed', 'Investment', inv.id, null, { amount, reference: payout.reference });
    return { status: 201, body: { message: 'Payout sent via Paystack.', data: { investment: investmentResource(inv), payout: payoutResource(payout) } } };
  }],

  ['POST', /^\/admin\/investments\/(\d+)\/payouts\/manual$/, ({ m, data }) => {
    const inv = db.investments.find((i) => i.id === Number(m[1]));
    if (!inv) throw apiError(404, 'Investment not found.');
    if (!['active', 'matured'].includes(inv.status)) throw apiError(422, 'Investment is not eligible for payout.');

    const amount = Number(readField(data, 'amount'));
    const payout = {
      id: db.nextPayoutId++,
      investment_id: inv.id,
      investor_id: inv.investor_id,
      amount,
      method: 'manual',
      reference: readField(data, 'reference', `MANUAL-OUT-${Date.now()}`),
      status: 'completed',
      note: readField(data, 'note', null),
      created_at: new Date().toISOString(),
    };
    db.payouts.push(payout);
    inv.total_interest_paid = (inv.total_interest_paid || 0) + amount;
    inv.last_accrued_at = new Date().toISOString();
    notify(inv.investor_id, 'payout.completed', 'Payout completed', `₦${amount.toLocaleString()} was paid out on ${inv.reference}.`);
    logAudit('payout.manual_recorded', 'Investment', inv.id, null, { amount, reference: payout.reference });
    return { status: 201, body: { message: 'Manual payout recorded.', data: { investment: investmentResource(inv), payout: payoutResource(payout) } } };
  }],

  // --- Investor statements ---
  ['GET', /^\/me\/payments$/, () => {
    const user = requireAuth();
    return { body: { data: db.payments.filter((p) => p.investor_id === user.id).map(paymentResource) } };
  }],

  ['GET', /^\/me\/payouts$/, () => {
    const user = requireAuth();
    return { body: { data: db.payouts.filter((p) => p.investor_id === user.id).map(payoutResource) } };
  }],

  // --- Admin: payments / payouts reports ---
  ['GET', /^\/admin\/payments$/, ({ params }) => {
    let list = [...db.payments].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    if (params?.method) list = list.filter((p) => p.method === params.method);
    return { body: paginate(list.map(paymentResource), params) };
  }],

  ['GET', /^\/admin\/payouts$/, ({ params }) => {
    let list = [...db.payouts].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    if (params?.method) list = list.filter((p) => p.method === params.method);
    return { body: paginate(list.map(payoutResource), params) };
  }],

  // --- Notifications ---
  ['GET', /^\/me\/notifications$/, () => {
    const user = requireAuth();
    const list = db.notifications
      .filter((n) => n.user_id === user.id)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return { body: { data: list } };
  }],

  ['PATCH', /^\/me\/notifications\/(\d+)\/read$/, ({ m }) => {
    const user = requireAuth();
    const n = db.notifications.find((x) => x.id === Number(m[1]) && x.user_id === user.id);
    if (!n) throw apiError(404, 'Not found.');
    n.read_at = new Date().toISOString();
    return { body: { data: n } };
  }],

  ['PATCH', /^\/me\/notifications\/read-all$/, () => {
    const user = requireAuth();
    const now = new Date().toISOString();
    db.notifications.filter((n) => n.user_id === user.id && !n.read_at).forEach((n) => { n.read_at = now; });
    return { body: { message: 'All notifications marked read.' } };
  }],

  // --- Support tickets: investor ---
  ['GET', /^\/me\/tickets$/, () => {
    const user = requireAuth();
    const list = db.tickets
      .filter((t) => t.investor_id === user.id)
      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    return { body: { data: list.map(ticketResource) } };
  }],

  ['GET', /^\/me\/tickets\/(\d+)$/, ({ m }) => {
    const user = requireAuth();
    const ticket = db.tickets.find((t) => t.id === Number(m[1]) && t.investor_id === user.id);
    if (!ticket) throw apiError(404, 'Not found.');
    return { body: { data: ticketResource(ticket) } };
  }],

  ['POST', /^\/me\/tickets$/, ({ data }) => {
    const user = requireAuth();
    const now = new Date().toISOString();
    const ticket = {
      id: db.nextTicketId++,
      investor_id: user.id,
      investor_name: user.full_name,
      subject: readField(data, 'subject'),
      category: readField(data, 'category', 'general'),
      status: 'open',
      messages: [{ id: db.nextTicketMessageId++, author: user.full_name, author_role: 'investor', body: readField(data, 'message'), created_at: now }],
      created_at: now,
      updated_at: now,
    };
    db.tickets.push(ticket);
    return { status: 201, body: { message: 'Ticket submitted.', data: ticketResource(ticket) } };
  }],

  ['POST', /^\/me\/tickets\/(\d+)\/messages$/, ({ m, data }) => {
    const user = requireAuth();
    const ticket = db.tickets.find((t) => t.id === Number(m[1]) && t.investor_id === user.id);
    if (!ticket) throw apiError(404, 'Not found.');
    const now = new Date().toISOString();
    ticket.messages.push({ id: db.nextTicketMessageId++, author: user.full_name, author_role: 'investor', body: readField(data, 'message'), created_at: now });
    ticket.updated_at = now;
    if (ticket.status === 'resolved' || ticket.status === 'closed') ticket.status = 'open';
    return { status: 201, body: { message: 'Reply sent.', data: ticketResource(ticket) } };
  }],

  // --- Support tickets: admin ---
  ['GET', /^\/admin\/tickets$/, ({ params }) => {
    let list = [...db.tickets].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    if (params?.status) list = list.filter((t) => t.status === params.status);
    return { body: paginate(list.map(ticketResource), params) };
  }],

  ['GET', /^\/admin\/tickets\/(\d+)$/, ({ m }) => {
    const ticket = db.tickets.find((t) => t.id === Number(m[1]));
    if (!ticket) throw apiError(404, 'Not found.');
    return { body: { data: ticketResource(ticket) } };
  }],

  ['POST', /^\/admin\/tickets\/(\d+)\/messages$/, ({ m, data }) => {
    const ticket = db.tickets.find((t) => t.id === Number(m[1]));
    if (!ticket) throw apiError(404, 'Not found.');
    const actor = currentUser();
    const now = new Date().toISOString();
    ticket.messages.push({ id: db.nextTicketMessageId++, author: actor?.full_name ?? 'Admin', author_role: actor?.roles?.[0] ?? 'admin', body: readField(data, 'message'), created_at: now });
    ticket.updated_at = now;
    if (ticket.status === 'open') ticket.status = 'in_progress';
    notify(ticket.investor_id, 'ticket.reply', 'Support replied to your ticket', `New reply on "${ticket.subject}".`);
    return { status: 201, body: { message: 'Reply sent.', data: ticketResource(ticket) } };
  }],

  ['PATCH', /^\/admin\/tickets\/(\d+)\/status$/, ({ m, data }) => {
    const ticket = db.tickets.find((t) => t.id === Number(m[1]));
    if (!ticket) throw apiError(404, 'Not found.');
    const before = ticket.status;
    ticket.status = readField(data, 'status');
    ticket.updated_at = new Date().toISOString();
    notify(ticket.investor_id, 'ticket.status', 'Ticket status updated', `"${ticket.subject}" is now ${ticket.status.replace('_', ' ')}.`);
    logAudit('ticket.status_updated', 'SupportTicket', ticket.id, { status: before }, { status: ticket.status });
    return { body: { message: 'Ticket status updated.', data: ticketResource(ticket) } };
  }],

  // --- Admin: audit log (read-only) ---
  ['GET', /^\/admin\/audit-logs$/, ({ params }) => {
    let list = [...db.auditLogs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    if (params?.action) list = list.filter((l) => l.action.includes(params.action));
    if (params?.auditable_type) list = list.filter((l) => l.auditable_type.includes(params.auditable_type));
    return { body: paginate(list, params) };
  }],

  // --- Super Admin: admin accounts ---
  ['GET', /^\/admin\/admin-users$/, () => {
    const admins = db.users.filter((u) => u.roles.includes('admin') || u.roles.includes('super_admin'));
    return { body: { data: admins.map(userResource) } };
  }],

  ['POST', /^\/admin\/admin-users$/, ({ data }) => {
    const user = {
      id: db.nextUserId++,
      full_name: readField(data, 'full_name'),
      email: readField(data, 'email'),
      roles: [readField(data, 'role', 'admin')],
      is_active: true,
      email_verified_at: new Date().toISOString(),
      created_manually_by_admin: true,
      created_at: new Date().toISOString(),
    };
    db.users.push(user);
    logAudit('admin_user.created', 'User', user.id, null, { email: user.email, role: user.roles[0] });
    return { status: 201, body: { message: 'Admin account created.', data: userResource(user) } };
  }],

  ['DELETE', /^\/admin\/admin-users\/(\d+)$/, ({ m }) => {
    db.users = db.users.filter((u) => u.id !== Number(m[1]));
    return { body: { message: 'Admin account removed.' } };
  }],
];

function logAudit(action, type, id, before, after) {
  const actor = currentUser();
  db.auditLogs.unshift({
    id: db.nextAuditId++,
    actor: actor?.full_name ?? 'System',
    actor_role: actor?.roles?.[0] ?? null,
    action,
    auditable_type: type,
    auditable_id: id,
    before,
    after,
    ip_address: '127.0.0.1',
    created_at: new Date().toISOString(),
  });
}

/** Resolves one mocked request. Mirrors axios's { data } / thrown-error shape so axiosBaseQuery can treat it identically to a real response. */
export async function resolveMockRequest({ url, method = 'GET', data, params }) {
  // Simulate realistic network latency so loading states are visible.
  await new Promise((r) => setTimeout(r, 220 + Math.random() * 200));

  const path = url.split('?')[0];
  const verb = method.toUpperCase();

  for (const [routeMethod, pattern, handler] of routes) {
    if (routeMethod !== verb) continue;
    const m = path.match(pattern);
    if (m) {
      const result = handler({ m, data, params });
      return { status: result.status ?? 200, data: result.body };
    }
  }

  throw apiError(404, `Mock route not found: ${verb} ${path}`);
}

export function resetMockDatabase() {
  db.users = clone(initialUsers);
  db.kycRecords = clone(initialKycRecords);
  db.investments = clone(initialInvestments);
  db.auditLogs = clone(initialAuditLogs);
  db.plans = clone(initialPlans);
  db.payments = clone(initialPayments);
  db.payouts = clone(initialPayouts);
  db.notifications = clone(initialNotifications);
  db.tickets = clone(initialTickets);
  setSessionUserId(null);
}
